import { formatBytes } from "Api/brandingAPI";

/**
 * The admin's quote on a hotel manual request: the fee fields, how they add
 * up, the stay's previously paid amount, and the quote's file. Shared by the
 * admin Modify / Cancel modals' Quote step, the Request tab's quote builder
 * (ModifyQuoteBuilderDialog) and the quote view (ModifyQuoteDialog).
 *
 *   modify  total      = base fare + tax + difference + penalty + service + markup
 *           amount due = total − previously paid   (negative → a refund)
 *   cancel  refund     = booking price (locked) − penalty − service − markup
 *           each deduction is capped at booking price − the other two, so a
 *           refund is never negative
 *
 * `quoteKit(kind)` gives either set one interface (fields, totals, validate,
 * buildPayload, draftFromQuote, setFee).
 */

/** The six fee fields, in display order, in the two cards of the form. */
export const FEE_GROUPS = [
  {
    key: "stay",
    title: "New stay price",
    fields: [
      { key: "baseFare", label: "Base fare" },
      { key: "tax", label: "Tax" },
    ],
  },
  {
    key: "charges",
    title: "Charges",
    fields: [
      { key: "differenceFee", label: "Difference fee" },
      { key: "penaltyFee", label: "Penalty fee" },
      { key: "serviceFee", label: "Service fee" },
      { key: "markupFee", label: "Markup fee" },
    ],
  },
];

export const FEE_FIELDS = FEE_GROUPS.flatMap((g) => g.fields);

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/** An empty draft — fee values are the strings the inputs hold. */
export const EMPTY_QUOTE_DRAFT = {
  fees: Object.fromEntries(FEE_FIELDS.map((f) => [f.key, ""])),
  attachment: null, // { name, contentType, size, data } | an existing quote's { …, url }
  attachmentError: "",
};

/** Digits and a single decimal point (ManualHotelBookingPage's rule): a
 *  pasted "-1230.50" becomes "1230.50" instead of vanishing. */
export const sanitizeMoney = (raw) => {
  const cleaned = String(raw == null ? "" : raw).replace(/[^0-9.]/g, "");
  const dot = cleaned.indexOf(".");
  if (dot === -1) return cleaned;
  return cleaned.slice(0, dot + 1) + cleaned.slice(dot + 1).replace(/[.]/g, "");
};

const feeNumber = (v) => {
  const s = String(v ?? "").trim();
  return s === "" ? 0 : Number(s);
};

/** Fee strings → numbers (empty = 0). */
export const breakdownOf = (fees) =>
  Object.fromEntries(FEE_FIELDS.map((f) => [f.key, round2(feeNumber(fees?.[f.key]))]));

/** Totals of a breakdown against what the stay already cost. */
export const quoteTotals = (breakdown, previouslyPaid) => {
  const total = round2(FEE_FIELDS.reduce((a, f) => a + (Number(breakdown?.[f.key]) || 0), 0));
  const paid = round2(previouslyPaid);
  const net = round2(total - paid);
  return { total, paid, net, amountDue: net > 0 ? net : 0, refundAmount: net < 0 ? -net : 0 };
};

const numberErrors = (fees, fields) => {
  const errors = {};
  fields.forEach((f) => {
    const s = String(fees?.[f.key] ?? "").trim();
    if (s === "") return;
    const n = Number(s);
    if (!Number.isFinite(n)) errors[f.key] = "Must be a number";
    else if (n < 0) errors[f.key] = "Must be ≥ 0";
  });
  return errors;
};

/** Field errors ({ key: message }), whether the draft can be sent, and the
 *  footer hint when it can't. */
export const validateQuoteDraft = (draft) => {
  const errors = numberErrors(draft?.fees, FEE_FIELDS);
  const { total } = quoteTotals(breakdownOf(draft?.fees), 0);
  const valid = Object.keys(errors).length === 0 && total > 0;
  return { errors, valid, empty: total <= 0, hint: total <= 0 ? "Enter an amount to send a quote" : "" };
};

/**
 * What the request's stays already cost, off the booking: each hotel's sales
 * total (its guests share the hotel's one fare row — counted once per hotel,
 * not per guest), else the booking total split evenly across its hotels.
 * Returns null when the booking says nothing usable.
 */
export const stayPaidAmount = (booking, hotelRowIds) => {
  const ids = Array.from(new Set((hotelRowIds || []).filter(Boolean)));
  if (!booking || !ids.length) return null;
  const count = (booking.hotelDetailsList || []).length || 1;
  const fallback = Number(booking.amount?.totalAmount) / count;
  let any = false;
  const sum = ids.reduce((acc, id) => {
    const pax = (booking.passengers || []).find((p) => p?._api?.hotelRowId === id);
    const fare = Number(pax ? booking.passengerFares?.[pax.id]?.sales?.total : NaN);
    if (Number.isFinite(fare) && fare > 0) {
      any = true;
      return acc + fare;
    }
    if (Number.isFinite(fallback) && fallback > 0) {
      any = true;
      return acc + fallback;
    }
    return acc;
  }, 0);
  return any ? round2(sum) : null;
};

const attachmentPayload = (att) =>
  att
    ? {
        attachment: {
          name: att.name,
          contentType: att.contentType,
          size: att.size,
          // A new file travels as a data URL; an untouched existing one is
          // kept by reference.
          ...(att.data ? { data: att.data } : {}),
          ...(att.url ? { url: att.url } : {}),
        },
      }
    : {};

/** The quote body sent with `create` / `quote/send` / `quote/update`. */
export const buildQuotePayload = (draft, previouslyPaid, currency) => {
  const breakdown = breakdownOf(draft?.fees);
  const { total, paid, amountDue, refundAmount } = quoteTotals(breakdown, previouslyPaid);
  return {
    currency: currency || "",
    breakdown,
    total,
    previouslyPaid: paid,
    amountDue,
    refundAmount,
    ...attachmentPayload(draft?.attachment),
  };
};

/** A draft prefilled from an existing quote (Edit quote). */
export const draftFromQuote = (quote) => ({
  fees: Object.fromEntries(
    FEE_FIELDS.map((f) => {
      const v = quote?.breakdown?.[f.key];
      return [f.key, Number(v) > 0 ? String(v) : ""];
    })
  ),
  attachment: quote?.attachment || null,
  attachmentError: "",
});

/* ── Refund quote (manual CANCELLATION) ──────────────────────────────── */

/** The three deductions from the (locked) booking price, in display order. */
export const CANCEL_DEDUCTIONS = [
  { key: "cancellationPenalty", label: "Cancellation penalty" },
  { key: "cancellationServiceFee", label: "Cancellation service fee" },
  { key: "cancellationMarkupFee", label: "Cancellation markup fee" },
];

export const EMPTY_CANCEL_DRAFT = {
  fees: Object.fromEntries(CANCEL_DEDUCTIONS.map((f) => [f.key, ""])),
  attachment: null,
  attachmentError: "",
};

const deductionsOf = (fees) =>
  Object.fromEntries(CANCEL_DEDUCTIONS.map((f) => [f.key, round2(feeNumber(fees?.[f.key]))]));

const sumOf = (obj, keys) => round2(keys.reduce((a, k) => a + (Number(obj?.[k]) || 0), 0));

/** Booking price − deductions; never negative. */
export const cancelTotals = (fees, bookingPrice) => {
  const d = deductionsOf(fees);
  const price = round2(Number(bookingPrice) || 0);
  const totalDeductions = sumOf(d, CANCEL_DEDUCTIONS.map((f) => f.key));
  return { bookingPrice: price, totalDeductions, refundAmount: round2(Math.max(price - totalDeductions, 0)), amountDue: 0 };
};

/** The most this deduction may be: booking price − the other two. */
export const deductionMax = (fees, key, bookingPrice) => {
  const others = sumOf(
    deductionsOf(fees),
    CANCEL_DEDUCTIONS.map((f) => f.key).filter((k) => k !== key)
  );
  return round2(Math.max((Number(bookingPrice) || 0) - others, 0));
};

/** What a deduction input may hold: digits, one dot, 2 decimals, and never
 *  more than its max — typing past it stops at the max. */
export const clampDeduction = (fees, key, raw, bookingPrice) => {
  let s = sanitizeMoney(raw);
  const dot = s.indexOf(".");
  if (dot !== -1) s = s.slice(0, dot + 3);
  if (s === "" || s === ".") return s;
  const max = deductionMax(fees, key, bookingPrice);
  return Number(s) > max ? String(max) : s;
};

export const validateCancelDraft = (draft, bookingPrice) => {
  const errors = numberErrors(draft?.fees, CANCEL_DEDUCTIONS);
  const priceMissing = !(Number(bookingPrice) > 0);
  if (!priceMissing) {
    CANCEL_DEDUCTIONS.forEach((f) => {
      if (errors[f.key]) return;
      if (feeNumber(draft?.fees?.[f.key]) > deductionMax(draft?.fees, f.key, bookingPrice)) {
        errors[f.key] = "More than the booking price allows";
      }
    });
  }
  const valid = !priceMissing && Object.keys(errors).length === 0;
  return {
    errors,
    valid,
    empty: false,
    priceMissing,
    hint: priceMissing ? "The booking price isn't on this booking" : "",
  };
};

export const buildCancelQuotePayload = (draft, bookingPrice, currency) => {
  const t = cancelTotals(draft?.fees, bookingPrice);
  return {
    currency: currency || "",
    breakdown: { bookingPrice: t.bookingPrice, ...deductionsOf(draft?.fees) },
    bookingPrice: t.bookingPrice,
    totalDeductions: t.totalDeductions,
    refundAmount: t.refundAmount,
    amountDue: 0,
    ...attachmentPayload(draft?.attachment),
  };
};

export const draftFromCancelQuote = (quote) => ({
  fees: Object.fromEntries(
    CANCEL_DEDUCTIONS.map((f) => {
      const v = quote?.breakdown?.[f.key];
      return [f.key, Number(v) > 0 ? String(v) : ""];
    })
  ),
  attachment: quote?.attachment || null,
  attachmentError: "",
});

/**
 * One interface over both quotes. `base` is what the form computes against:
 * modify — what the stays were paid (previouslyPaid); cancel — the locked
 * booking price.
 */
const KITS = {
  modify: {
    kind: "modify",
    emptyDraft: EMPTY_QUOTE_DRAFT,
    setFee: (fees, key, raw) => ({ ...fees, [key]: sanitizeMoney(raw) }),
    validate: (draft) => validateQuoteDraft(draft),
    buildPayload: (draft, base, currency) => buildQuotePayload(draft, base, currency),
    draftFromQuote,
  },
  cancel: {
    kind: "cancel",
    emptyDraft: EMPTY_CANCEL_DRAFT,
    setFee: (fees, key, raw, base) => ({ ...fees, [key]: clampDeduction(fees, key, raw, base) }),
    validate: (draft, base) => validateCancelDraft(draft, base),
    buildPayload: (draft, base, currency) => buildCancelQuotePayload(draft, base, currency),
    draftFromQuote: draftFromCancelQuote,
  },
};

export const quoteKit = (kind) => (kind === "cancel" ? KITS.cancel : KITS.modify);

/* ── Quote file ──────────────────────────────────────────────────────── */

export const QUOTE_ATTACHMENT_TYPES = ["application/pdf", "image/jpeg", "image/png"];
export const QUOTE_ATTACHMENT_ACCEPT = QUOTE_ATTACHMENT_TYPES.join(",");
/** The backend's existing cap for documents (partner signup, finance). */
export const QUOTE_ATTACHMENT_MAX_BYTES = 4 * 1024 * 1024;

/** { file, error } — the FinancePage rule, narrowed to PDF / JPEG / PNG. */
export const validateQuoteAttachment = (file) => {
  if (!file) return { file: null, error: "" };
  if (!QUOTE_ATTACHMENT_TYPES.includes(file.type)) {
    return { file: null, error: "PDF, JPEG or PNG only." };
  }
  if (file.size > QUOTE_ATTACHMENT_MAX_BYTES) {
    const mb = (file.size / (1024 * 1024)).toFixed(1);
    return { file: null, error: `The file is ${mb} MB. The limit is 4 MB.` };
  }
  return { file, error: "" };
};

export const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error("Could not read file."));
    reader.readAsDataURL(file);
  });

/** "quote.pdf · 312 KB" */
export const attachmentLabel = (att) =>
  att ? [att.name || "Attachment", Number(att.size) > 0 ? formatBytes(Number(att.size)) : ""].filter(Boolean).join(" · ") : "";

/** Can this attachment be opened (a hosted url, or the file's own data)? */
export const canOpenAttachment = (att) => !!(att?.url || att?.data);

/**
 * Open the quote's file in a new tab. A hosted `url` opens as is; a data URL
 * becomes a Blob first — browsers refuse to open data: URLs at the top level.
 */
export const openQuoteAttachment = (att) => {
  if (!att) return;
  if (att.url) {
    window.open(att.url, "_blank", "noopener,noreferrer");
    return;
  }
  if (!att.data) return;
  try {
    const [head, body] = String(att.data).split(",");
    const type = (head.match(/data:([^;]+)/) || [])[1] || att.contentType || "application/octet-stream";
    const bin = atob(body || "");
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
    const href = URL.createObjectURL(new Blob([bytes], { type }));
    // Our own same-origin blob — `noopener` would make some browsers refuse it.
    window.open(href, "_blank");
    window.setTimeout(() => URL.revokeObjectURL(href), 60000);
  } catch {
    // unreadable data — nothing to open
  }
};
